<template>
  <div class="dropdownContainer min-w-[140px]" :class="props.id">
    <label v-if="props.label" class="block mb-[8px] title-16 text-primary-dark">
      {{ props.label }}
    </label>
    <div
      v-if="disabled"
      class="relative flex items-center leading-[20px] text-[14px] min-h-[42px] cursor-pointer bg-[#F3F2F2] px-2.5 border border-grey-light rounded-[6px]"
    >
      <!-- change input to div to prevent submitting -->
      <div
        v-if="!selectedOption"
        class="text-nowrap text-[14px] text-grey-light font-normal pr-[30px] align-middle"
        :class="[hiddenOption ? 'text-primary-dark font-normal' : '']"
      >
        {{ props.placeholder }}
      </div>
      <div v-else class="">{{ selectedOption }}</div>
    </div>
    <div
      v-if="!disabled"
      ref="selectRef"
      class="relative flex items-center leading-[20px] text-[14px] min-h-[42px] cursor-pointer bg-white px-2.5 border border-grey-light rounded-[6px]"
      :class="(inputClass, error ? '!border-[#EB5554]' : '')"
      @click="toggleDropdown($event)"
    >
      <!--  กรณี :searchable="true" -->
      <input
        v-if="props.searchable"
        v-model="searchQuery"
        type="text"
        :placeholder="inputPlaceholder"
        :class="[
          'placeholder-input text-nowrap align-middle label-L bg-transparent border-none outline-none w-full',
          !searchQuery ? props.placeholderClass : '!text-[primary-dark]',
        ]"
      />

      <!--  กรณีไม่ :searchable="true" และยังไม่มี selected -->
      <div
        v-else-if="!selectedOption"
        class="text-nowrap text-[14px] font-normal pr-[30px] align-middle"
        :class="[
          props.placeholderClass,
          hiddenOption ? 'text-primary-dark font-normal' : '',
        ]"
      >
        {{ props.placeholder }}
      </div>

      <!--  กรณี :searchable="false" และเลือกแล้ว -->
      <span
        v-else
        class="text-nowrap align-middle text-[#1F1D1F] label-L"
        :class="[hiddenOption ? '!text-[primary-dark]' : '']"
      >
        {{ selectedOption }}
      </span>
      <!-- <CloseIcon @click.stop="removeSelectedOption" v-if="deleteable" class="w-6 h-6 absolute right-10 z-50 hover:text-red-800"/> -->
      <div
        class="caret-down absolute end-4 top-1/2 -translate-y-1/2 duration-200"
        :class="dropdownIsOpened ? 'rotate-180' : ''"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M16.6763 6.32372C17.1079 6.75536 17.1079 7.45517 16.6763 7.8868L10.7815 13.7815C10.3499 14.2132 9.65009 14.2132 9.21846 13.7815L3.32372 7.8868C2.89209 7.45517 2.89209 6.75536 3.32372 6.32372C3.75536 5.89209 4.45517 5.89209 4.8868 6.32372L10 11.4369L15.1132 6.32372C15.5448 5.89209 16.2446 5.89209 16.6763 6.32372Z"
            fill="#0B2D3C"
          />
        </svg>
      </div>

      <Teleport to="body">
        <div
          v-if="dropdownIsOpened"
          ref="dropdownRef"
          :id="`dropdown-${instanceId}`"
          class="dropdown bg-white rounded-[6px] overflow-hidden"
          :class="optionClass"
          :style="dropdownStyle"
          @click.stop
        >
          <ul>
            <li
              v-for="(option, key) of filteredOptions"
              :key="key"
              :id="option.id"
              class="label-L p-[10px] cursor-pointer"
              :class="[
                'hover:bg-soft-pink',
                isOptionDisabled(option)
                  ? 'opacity-50 cursor-not-allowed pointer-events-none'
                  : '',
              ]"
              @click="!isOptionDisabled(option) && handleOnChange(option)"
              :hidden="hideChiangRai ? option.id === 46 : false"
            >
              <span v-if="typeof option === 'string'">{{ option }}</span>
              <span v-else-if="typeof option === 'object'">
                <span>{{ option[props.optionToDisplay] }}</span>
              </span>
            </li>
            <li
              v-if="props.searchable && filteredOptions?.length === 0"
              class="label-L !text-[#B7B2B5] px-[10px] py-[8px]"
            >
              ไม่พบข้อมูล
            </li>
          </ul>
        </div>
      </Teleport>
    </div>
    <small class="textError label-M mt-2">{{
      Array.isArray(error) ? error[0] : error
    }}</small>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed, onUnmounted } from "vue";
import provinceData from "@/assets/json/provinces.json";
import districtData from "@/assets/json/districts.json";
import subDistrictData from "@/assets/json/subdistricts.json";
import { useDropdownManager } from "@/composables/useDropdownManager";

const emit = defineEmits(["update:modelValue", "change", "resetSubdistrict"]);
const props = defineProps({
  id: {
    required: false,
    default: null,
  },
  label: "",
  placeholder: "โปรดเลือก",
  options: {
    type: Array,
    default: () => [],
  },
  error: null,
  inputClass: "",
  optionToDisplay: {
    type: String,
    default: "name",
  },
  valueKey: {
    type: String,
    default: "",
  },
  modelValue: [String, Object, Number],
  provinceCode: "",
  disabled: Boolean,
  districtCode: {
    type: [String, Number],
    default: "",
  },
  hiddenOption: Boolean,
  removable: Boolean,
  hideChiangRai: Boolean,
  deleteable: {
    type: Boolean,
    default: false,
  },
  placeholderClass: {
    type: String,
    default: "text-grey-light",
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  optionClass: {
    type: String,
    require: false,
  },
});

const instanceId = ref(Math.random().toString(36).substring(2, 11));

// Use dropdown manager for coordinated dropdown behavior
const { registerDropdown } = useDropdownManager();
const {
  isOpen: dropdownIsOpened,
  toggleDropdown: toggle,
  closeDropdown,
} = registerDropdown(instanceId.value);

const dropdownPosition = ref({ top: 0, left: 0, width: 0 });
const dropdownRef = ref(null);
const selectRef = ref(null);
const selectedOption = ref(null);
const searchQuery = ref("");

const updateDropdownPosition = () => {
  if (selectRef.value) {
    const rect = selectRef.value.getBoundingClientRect();
    dropdownPosition.value = {
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    };
  }
};

const dropdownStyle = computed(() => ({
  position: "absolute",
  top: `${dropdownPosition.value.top}px`,
  left: `${dropdownPosition.value.left}px`,
  width: `${dropdownPosition.value.width}px`,
  zIndex: 9999,
}));

const toggleDropdown = (event) => {
  event?.stopPropagation();
  if (!dropdownIsOpened.value) {
    updateDropdownPosition();
  }
  toggle();
};

// Keep the closeDropdown function for external use
// const closeDropdown = () => {
//   dropdownIsOpened.value = false;
// };

const options = computed(() => {
  if (props.id === "provinceSelector") {
    return [{ provinceNameTh: props.placeholder }, ...provinceData];
  } else if (props.id === "districtSelector") {
    const filtered = districtData.filter(
      (d) => d.provinceCode == props.provinceCode
    );
    return [
      { districtNameTh: "แสดงทุกอำเภอ", districtCode: null },
      ...filtered,
    ];
  } else if (props.id === "subdistrictSelector") {
    !!props.districtCode ? "" : (selectedOption.value = "");
    return !!props.districtCode
      ? subDistrictData.filter((sub) => sub.districtCode == props.districtCode)
      : [];
  }
  return [ ...props.options];
});

// const getOptionLabel = (option) =>
//   typeof option === "object" ? option.label : option;

const handleSelection = (option) => {
  if (props.id == "districtSelector" && option.districtCode === null) {
    selectedOption.value = "";
    emit("update:modelValue", "");
    emit("resetSubdistrict", "");
    return;
  }
  if (typeof option === "string") {
    selectedOption.value = option;
    emit("update:modelValue", option);
    if (props.searchable) searchQuery.value = option;
  } else if (typeof option === "object") {
    selectedOption.value = option[props.optionToDisplay] || "";
    emit("update:modelValue", option[props.valueKey] || "");
    if (props.searchable) searchQuery.value = option[props.optionToDisplay];
  }
};

const handleOnChange = (option) => {
  if (isOptionDisabled(option)) return; // กันอีกรอบ
  emit("change", option);
  handleSelection(option);
  closeDropdown();
};

const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value) return options.value;
  return options.value.filter((option) => {
    const label =
      typeof option === "object" ? option[props.optionToDisplay] : option;
    return label.toLowerCase().includes(searchQuery.value.toLowerCase());
  });
});

const inputPlaceholder = computed(() =>
  !selectedOption.value || searchQuery.value === "" ? props.placeholder : ""
);

const handleOutsideClick = (event) => {
  const dropdownElement = document.getElementById(
    `dropdown-${instanceId.value}`
  );

  if (
    selectRef.value &&
    !selectRef.value.contains(event.target) &&
    dropdownElement &&
    !dropdownElement.contains(event.target)
  ) {
    closeDropdown();
  }
};

// FIXED: Handle window events
const handleWindowEvents = () => {
  if (dropdownIsOpened.value) {
    updateDropdownPosition();
  }
};

const resolveFromModelValue = (val) => {
  if (!val) {
    selectedOption.value = "";
    if (props.searchable) searchQuery.value = "";
    return;
  }
  const mv = val;
  const matched = options.value?.find((opt) => {
    if (typeof opt === "object") {
      if (props.valueKey) {
        // เทียบแบบไม่แคร์ type (กันเคส "3" vs 3)
        return String(opt[props.valueKey]) === String(mv);
      }
      // fallback เผื่อใช้ฟิลด์อื่น
      return (
        String(opt[props.valueKey]) === String(mv) ||
        String(opt.id) === String(mv) ||
        String(opt.value) === String(mv)
      );
    } else {
      return String(opt) === String(mv);
    }
  });

  if (matched) {
    handleSelection(matched);
    if (props.searchable && typeof matched === "object") {
      searchQuery.value = matched[props.optionToDisplay];
    }
  } else {
    selectedOption.value = "";
    if (props.searchable) searchQuery.value = "";
    // console.warn(`No matching option found for value: ${mv} in component: ${props.id}`)
  }
};

onMounted(() => {
  document.addEventListener("click", handleOutsideClick);
  window.addEventListener("scroll", handleWindowEvents, true);
  window.addEventListener("resize", handleWindowEvents);
});

onUnmounted(() => {
  document.removeEventListener("click", handleOutsideClick);
  window.removeEventListener("scroll", handleWindowEvents, true);
  window.removeEventListener("resize", handleWindowEvents);
});

watch(
  [() => props.modelValue, options],
  ([newVal]) => {
    resolveFromModelValue(newVal);
  },
  { immediate: true }
);

// check disabled
// option.disable === 1 ||
const isOptionDisabled = (option) =>
  typeof option === "object" &&
  (option.disabled === true);
</script>

<style scoped lang="scss">
.dropdown {
  position: absolute;
  width: 100%;
  margin-top: 5px;
  top: 25px;
  left: 0;
  z-index: 100;
  box-shadow: 0px 6px 12px 0px rgba(0, 0, 0, 0.25);
  overflow: auto;
  max-height: 200px;
  padding: 4px;

  li {
    border-radius: 4px;
    color: var(--color-primary-dark);
  }
}

.placeholder-input::placeholder {
  color: #1f1d1f;
}
</style>
